import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ── Icons ──
const CalIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="1.5" y="2.5" width="13" height="12" rx="1.5"/>
    <path d="M5 1v3M11 1v3M1.5 7h13"/>
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="8" cy="8" r="6.5"/>
    <path d="M8 4.5V8l2.5 2"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
    <circle cx="8" cy="8" r="2"/>
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9" cy="9" r="6"/><path d="M15 15l3 3"/>
  </svg>
);
const ArrowIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 8h10M9 4l4 4-4 4"/>
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 14 14" width="11" height="11" fill="currentColor">
    <path d="M6.5 1a1 1 0 01.707.293l5.5 5.5a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-5.5-5.5A1 1 0 012 6V2a1 1 0 011-1h3.5zM4.5 4a.5.5 0 100-1 .5.5 0 000 1z"/>
  </svg>
);

// ── Blog Card Component ──
function BlogCard({ blog, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);


  return (
    <div
      ref={ref}
      onClick={() => navigate(`/blog/${blog._id}`)}
      className="group flex flex-col rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.5s ease ${index * 0.07}s, transform 0.5s ease ${index * 0.07}s, box-shadow 0.2s`,
        background: "white",
        border: "1.5px solid #e8f0e0",
        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: "180px" }}>
        <img src={blog.image} alt={blog.title}
          className="w-full h-full object-cover transition-transform duration-600 group-hover:scale-105" />
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors" />
        <span className="absolute top-3 left-3 flex items-center bg-green-400 gap-1 text-xs font-bold px-2.5 py-1 rounded-full"
          >
          <TagIcon /> {blog.author}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-black text-gray-900 leading-snug mb-2 group-hover:text-green-700 transition-colors"
          style={{ fontSize: "clamp(14px, 1.5vw, 16px)" }}>
          {blog.title || "Undefined"}
        </h3>
        {/* <p >{blog.content}</p> */}
        <div className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4 flex-1"
        dangerouslySetInnerHTML={{__html : blog.content}}
        />

        {/* Meta */}
        <div className="pt-3 flex items-center justify-between" style={{ borderTop: "0.5px solid #e8f0e0" }}>
          <div className="flex items-center gap-2">
            <div>
              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                <CalIcon /> {blog?.updatedAt &&
                        new Date(blog.updatedAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                        })}
              </p>
            </div>
          </div>
          <div>
            <p className="text-sm font-josefin font-medium">View More</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Blog Listing Page ──
export default function BlogPage() {
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [blog , setBlog] = useState([]);
  const [loading , setLoading] = useState(false);



  const getAllBlogs = async()=>{
    try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog/get-all`);
        // console.log("Data", res.data);
        setBlog(res.data || []);
        
    } catch (error) {
        console.log("Error is occured", error);
    }finally{
        setLoading(false);
    }
  }

  useEffect(()=>{
    getAllBlogs();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#f8faf6", fontFamily: "'Barlow', sans-serif" }}>

      <div className="px-6 md:px-10 lg:px-12 xl:px-20  py-10">
        {/* ── Controls ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search articles, topics, authors..."
              className="w-full pl-11 pr-4 py-3 text-sm rounded-xl border outline-none transition-all"
              style={{ borderColor: "#e0ead8", background: "white", fontFamily: "inherit" }}
              onFocus={e => e.target.style.borderColor = "#4ade80"}
              onBlur={e => e.target.style.borderColor = "#e0ead8"} />
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="text-xs font-semibold px-4 py-2 rounded-xl border outline-none"
            style={{ borderColor: "#e0ead8", background: "white", color: "#374151", fontFamily: "inherit" }}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="popular">Most popular</option>
          </select>
        </div>

        {/* Count */}
        <p className="text-sm text-gray-500 mb-6">
          <strong className="text-gray-800">{blog.length}</strong> article{blog.length !== 1 ? "s" : ""}
          {activeTag !== "All" && ` in ${activeTag}`}
          {search && ` matching "${search}"`}
        </p>

        {/* ── Grid ── */}
        {blog.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <p className="text-5xl mb-4">📝</p>
            <p className="font-black text-gray-600 text-lg mb-2">No articles found</p>
            <button onClick={() => { setSearch(""); setActiveTag("All"); }}
              className="mt-4 text-sm font-bold px-5 py-2.5 rounded-xl"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}>
              Reset filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.isArray(blog)&& blog.map((blog, i) => (
              <BlogCard key={blog.id} blog={blog} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}